// AICA Badge Component - Meridian Dark Theme
// Enhanced badge with icons, dismissible, dot indicator, and multiple variants
import React, { forwardRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface BadgeProps {
  className?: string;
  children?: React.ReactNode;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  shape?: 'rounded' | 'pill' | 'square';
  outline?: boolean;
  pulse?: boolean;
  dot?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  dismissible?: boolean;
  onDismiss?: () => void;
  asButton?: boolean;
  /**
   * Element of the plain (non-button) badge. `span` where the badge sits in
   * phrasing content — a `<p>`, a `<button>` — that a `<div>` would make
   * invalid markup.
   */
  as?: 'div' | 'span';
  onClick?: React.MouseEventHandler<HTMLElement>;
  'data-testid'?: string; // E2E testing ID
}

export const Badge = forwardRef<HTMLElement, BadgeProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      shape = 'rounded',
      outline = false,
      pulse = false,
      dot = false,
      icon,
      iconPosition = 'left',
      dismissible = false,
      onDismiss,
      asButton = false,
      as: Tag = 'div',
      children,
      onClick,
      'data-testid': testId,
    },
    ref
  ) => {
    const variants = {
      default: outline
        ? 'border-border text-fg-muted bg-transparent'
        : 'bg-bg-accent text-fg-muted border-border',
      primary: outline
        ? 'border-primary text-primary bg-transparent'
        : 'bg-primary-dim text-primary-fg border-primary/30',
      success: outline
        ? 'border-success text-success bg-transparent'
        : 'bg-success-dim text-success-fg border-success/30',
      warning: outline
        ? 'border-warning text-warning bg-transparent'
        : 'bg-warning-dim text-warning-fg border-warning/30',
      danger: outline
        ? 'border-danger text-danger bg-transparent'
        : 'bg-danger-dim text-danger-fg border-danger/30',
      info: outline
        ? 'border-info text-info bg-transparent'
        : 'bg-info-dim text-info-fg border-info/30',
    };

    const sizes = {
      xs: 'px-1 py-0.5 text-2xs gap-0.5',
      sm: 'px-1.5 py-0.5 text-micro gap-1',
      md: 'px-2 py-0.5 text-label gap-1',
      lg: 'px-2.5 py-1 text-xs gap-1.5',
    };

    const shapes = {
      rounded: 'rounded',
      pill: 'rounded-full',
      square: 'rounded-none',
    };

    const dotSizes = {
      xs: 'w-1 h-1',
      sm: 'w-1.5 h-1.5',
      md: 'w-2 h-2',
      lg: 'w-2.5 h-2.5',
    };

    const isButton = asButton || onClick;

    const commonClassName = cn(
      // No blanket text-transform: badge labels come from i18n already cased per
      // locale. `capitalize` Title-Cased every word and mangled non-English
      // labels ("Нет карты" → "Нет Карты", "Не реализовано" → "Не Реализовано").
      // Badges that render raw lowercase slugs (provider keys) opt back in via
      // an explicit `capitalize` className.
      'inline-flex items-center font-semibold',
      'border transition-all duration-150',
      'relative',
      shapes[shape],
      variants[variant],
      sizes[size],
      // Interactive states
      isButton && [
        'cursor-pointer',
        'hover:shadow-[var(--glow-subtle)] hover:scale-[1.02]',
        'active:scale-[0.98]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
      ],
      // Pulse animation for "live" status
      pulse && 'animate-pulse',
      className
    );

    const content = (
      <>
        {/* Dot indicator */}
        {dot && (
          <span
            className={cn(
              'rounded-full',
              variant === 'default' ? 'bg-fg-muted' : `bg-current`,
              dotSizes[size],
              pulse && 'animate-ping absolute',
              !pulse && 'relative'
            )}
          />
        )}

        {/* Left icon */}
        {icon && iconPosition === 'left' && (
          <span className="inline-flex shrink-0">{icon}</span>
        )}

        {/* Content */}
        <span className="truncate">{children}</span>

        {/* Right icon */}
        {icon && iconPosition === 'right' && (
          <span className="inline-flex shrink-0">{icon}</span>
        )}

        {/* Dismiss button */}
        {dismissible && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss?.();
            }}
            className={cn(
              'inline-flex shrink-0 ml-0.5 -mr-0.5',
              'hover:opacity-70 transition-opacity',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 rounded'
            )}
            aria-label="Dismiss"
          >
            <X
              className={cn(
                size === 'xs' && 'w-2.5 h-2.5',
                size === 'sm' && 'w-3 h-3',
                size === 'md' && 'w-3.5 h-3.5',
                size === 'lg' && 'w-4 h-4'
              )}
            />
          </button>
        )}
      </>
    );

    if (isButton) {
      return (
        <button
          ref={ref as React.RefObject<HTMLButtonElement>}
          className={commonClassName}
          onClick={onClick}
          type="button"
          data-testid={testId}
        >
          {content}
        </button>
      );
    }

    return (
      <Tag
        ref={ref as React.Ref<HTMLDivElement & HTMLSpanElement>}
        className={commonClassName}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e: React.KeyboardEvent<HTMLElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.currentTarget.click();
                }
              }
            : undefined
        }
        data-testid={testId}
      >
        {content}
      </Tag>
    );
  }
);

Badge.displayName = 'Badge';
